import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { useAuth } from '../../context/AuthContext';
import { fetchNodes, fetchPointsWithReadings } from '../../services/assets';
import { Search, Filter, ChevronRight, X, SlidersHorizontal, RefreshCwOff, Gauge, AlertTriangle, Clipboard } from 'lucide-react';
import { useWebSocket } from '../../context/WebSocketContext';

function PointList() {
  const { theme } = useTheme();
  const { currentSiteId, permissions, userSites } = useAuth();
  const [points, setPoints] = useState([]);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    pageSize: 20,
    totalPages: 1,
    totalItems: 0
  });
  const canViewRealTimeData = userSites.some(site => site.site_id === currentSiteId && site.supports_real_time_data);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationLevels, setLocationLevels] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [enabledColumns, setEnabledColumns] = useState(['name', 'location', 'value', 'diagnosis', canViewRealTimeData? '' : 'tag']);
  const [copied, setCopied] = useState({});
  const { subscribe, unsubscribe } = useWebSocket();
  const [pointReadings, setPointReadings] = useState({});
  const [qualityFilter, setQualityFilter] = useState('All');
  const [lastUpdatedFilter, setLastUpdatedFilter] = useState('All');
  const [diagnosisFilter, setDiagnosisFilter] = useState('All');
  const [natureFilter, setNatureFilter] = useState('All');
  const [stagnantPointsCount, setStagnantPointsCount] = useState(0);
  const [lowQualityPointsCount, setLowQualityPointsCount] = useState(0);

  // Handle WebSocket updates
  const handleUpdate = useCallback((data) => {
    if (!canViewRealTimeData) return;
    
    setPointReadings(prev => ({
      ...prev,
      [data.tag]: {
        value: data.value,
        quality: data.quality,
        data_type: data.data_type
      }
    }));
  }, [canViewRealTimeData]);

  // Subscribe to point readings
  useEffect(() => {
    if (!canViewRealTimeData) return;

    points.forEach(point => {
      if (point.tag) {
        subscribe(point.tag, ['readings'], handleUpdate);
      }
    });

    return () => {
      points.forEach(point => {
        if (point.tag) {
          unsubscribe(point.tag, handleUpdate);
        }
      });
    };
  }, [points, subscribe, unsubscribe, handleUpdate, canViewRealTimeData]);

  // Fetch initial location nodes (first level)
  useEffect(() => {
    const loadInitialLocations = async () => {
      try {
        const nodes = await fetchNodes(currentSiteId, null, true, ['Location', 'Asset']);
        setLocationLevels([nodes]);
        if (nodes.length > 0) {
          setSelectedLocations([nodes[0]]);
          if (nodes[0].children?.length > 0) {
            setLocationLevels(prev => [...prev, nodes[0].children]);
          }
        }
      } catch (error) {
        console.error('Error loading locations:', error);
      }
    };
    loadInitialLocations();
  }, [currentSiteId]);

  // Load points based on filters
  useEffect(() => {
    const loadPoints = async () => {
      if(!selectedLocations.length) {
        return;
      }
      setLoading(true);
      try {
        const lastSelectedLocation = selectedLocations[selectedLocations.length - 1];
        const selectedLocationId = lastSelectedLocation?.id;
        
        const response = await fetchPointsWithReadings(
          currentSiteId,
          selectedLocationId,
          searchQuery,
          pagination.currentPage,
          pagination.pageSize,
          null,
          qualityFilter === 'All' ? null : qualityFilter,
          lastUpdatedFilter === 'All' ? null : lastUpdatedFilter,
          diagnosisFilter === 'All' ? null : diagnosisFilter,
          natureFilter === 'All' ? null : natureFilter
        );
        
        setPoints(response.items);
        setPagination(response.pagination);
        setStagnantPointsCount(response.stagnantPointsCount);
        setLowQualityPointsCount(response.lowQualityPointsCount);

      } catch (error) {
        console.error('Error loading points:', error);
      }
      setLoading(false);
    };

    loadPoints();
  }, [currentSiteId, selectedLocations, searchQuery, pagination.currentPage, pagination.pageSize, qualityFilter, lastUpdatedFilter, diagnosisFilter, natureFilter]);

  const handleLocationSelect = async (node, level) => {
    if (!node && level > 0) {
      setSelectedLocations(prev => prev.slice(0, level));
      setLocationLevels(prev => prev.slice(0, level + 1));
      setPagination(prev => ({ ...prev, currentPage: 1 }));
      return;
    }

    setSelectedLocations(prev => {
      const newSelected = [...prev.slice(0, level)];
      if (node) {
        newSelected[level] = node;
      }
      return newSelected;
    });
    setPagination(prev => ({ ...prev, currentPage: 1 }));

    if (node && level > 0) {
      try {
        const nodes = await fetchNodes(currentSiteId, node.id, true, ['Location', 'Asset']);
        setLocationLevels(prev => {
          const newLevels = [...prev.slice(0, level + 1)];
          newLevels[level + 1] = nodes;
          return newLevels;
        });
      } catch (error) {
        console.error('Error loading child locations:', error);
      }
    } else if (node) {
      setLocationLevels(prev => {
        const newLevels = [...prev.slice(0, level + 1)];
        if (node.children?.length > 0) {
          newLevels[level + 1] = node.children;
        }
        return newLevels;
      });
    }
  };

  // Helper function to generate page numbers with ellipsis
  const getPageNumbers = () => {
    const pages = [];
    const total = pagination.totalPages;
    const current = pagination.currentPage;
    const maxPagesToShow = 3;
    const half = Math.floor(maxPagesToShow / 2);

    let start = Math.max(1, current - half);
    let end = Math.min(total, current + half);

    if (current - 1 <= half) {
      end = Math.min(total, maxPagesToShow);
    }
    if (total - current <= half) {
      start = Math.max(1, total - maxPagesToShow + 1);
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (start > 2) {
      pages.unshift('...');
      pages.unshift(1);
    } else if (start === 2) {
      pages.unshift(1);
    }

    if (end < total - 1) {
      pages.push('...');
      pages.push(total);
    } else if (end === total - 1) {
      pages.push(total);
    }

    return pages;
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopied({ ...copied, [id]: true });
    setTimeout(() => setCopied({ ...copied, [id]: false }), 500);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] p-2 md:p-4 space-y-2 md:space-y-4 w-[calc(100vw-16px)] overflow-auto">
      {/* Search and Filter Bar */}
      <div className="flex flex-col space-y-2">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 w-full">
          <div className={`w-full md:flex-1 flex items-center px-2 md:px-3 py-2 rounded-lg border backdrop-blur-sm ${
            theme === 'light' ? 'border-gray-300/50 bg-white/80' : 'border-gray-700/50 bg-gray-800/80'
          }`}>
            <Search className="w-4 md:w-5 h-4 md:h-5 text-blue-500" />
            <input
              type="text"
              placeholder="Search points by name or tag..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPagination(prev => ({ ...prev, currentPage: 1 })); }}
              className={`ml-2 outline-none w-full text-sm md:text-base ${
                theme === 'light' ? 'text-gray-900 bg-transparent' : 'text-gray-100 bg-transparent'
              }`}
            />
            {searchQuery && (
              <button 
                onClick={() => { setSearchQuery(''); setPagination(prev => ({ ...prev, currentPage: 1 })); }}
                className="text-blue-500 hover:text-blue-600"
              >
                <X className="w-4 md:w-5 h-4 md:h-5" />
              </button>
            )}
          </div>

          {/* Location Filters */}
          <div className="w-full md:w-auto flex flex-wrap items-center gap-2">
            {locationLevels.map((nodes, level) => {
              if (level === 0 || (locationLevels[level] && locationLevels[level].length > 0)) {
                return (
                  <React.Fragment key={level}>
                    <select
                      value={selectedLocations[level]?.id || ''}
                      onChange={(e) => {
                        const selectedId = e.target.value;
                        const selected = selectedId ? nodes.find(n => n.id === parseInt(selectedId)) : null;
                        handleLocationSelect(selected, level);
                      }}
                      className={`flex-1 md:flex-none text-sm px-2 md:px-3 py-1 md:py-2 rounded-lg border backdrop-blur-sm ${
                        theme === 'light' 
                          ? 'border-gray-300/50 bg-white/80 text-gray-900' 
                          : 'border-gray-700/50 bg-gray-800/80 text-gray-100'
                      }`}
                    >
                      {level === 0 ? (
                        nodes.map(node => (
                          <option key={node.id} value={node.id}>{node.name}</option>
                        ))
                      ) : (
                        <>
                          <option value="">All</option>
                          {nodes.map(node => (
                            <option key={node.id} value={node.id}>{node.name}</option>
                          ))}
                        </>
                      )}
                    </select>
                    {level < locationLevels.length - 1 && 
                     locationLevels[level + 1]?.length > 0 && 
                     selectedLocations[level] && (
                      <ChevronRight className="hidden md:block w-4 h-4 text-blue-500" />
                    )}
                  </React.Fragment>
                );
              }
              return null;
            })}
          </div>

          {/* Column Toggle */}
          <button
            onClick={() => setEnabledColumns(prev => 
              prev.includes('tag') ? prev.filter(col => col !== 'tag') : [...prev, 'tag']
            )}
            className={`px-2 md:px-3 py-1 md:py-2 text-sm rounded-lg border backdrop-blur-sm ${
              theme === 'light'
                ? 'border-gray-300/50 hover:bg-gray-50/50'
                : 'border-gray-700/50 hover:bg-gray-800/50'
            } ${
              enabledColumns.includes('tag') ? 'bg-blue-500/10 text-blue-500 border-blue-500/30' : ''
            }`}
          >
            <Filter className="w-4 h-4 inline-block mr-2" />
            Tag
          </button>
        </div>

        {/* Stats and Additional Filters */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2">
          {/* Stats */}
          <div className="flex items-center gap-6 px-2">
            <div className="flex items-center text-sm">
              <RefreshCwOff className="w-4 h-4 text-yellow-600 mr-2" />
              <span className="hidden md:inline">
                <span className={theme === 'light' ? 'text-gray-600' : 'text-gray-400'}>Stagnant Points:</span>
                <span className="text-yellow-600 font-medium ml-2">
                  {((stagnantPointsCount / pagination.totalItems) * 100).toFixed(1)}% ({stagnantPointsCount} points)
                </span>
              </span>
              <span className="md:hidden text-yellow-600 font-medium ml-2">
                {((stagnantPointsCount / pagination.totalItems) * 100).toFixed(1)}% ({stagnantPointsCount} pnts)
              </span>
            </div>
            <div className="flex items-center text-sm">
              <AlertTriangle className="w-4 h-4 text-pink-600 mr-2" />
              <span className="hidden md:inline">
                <span className={theme === 'light' ? 'text-gray-600' : 'text-gray-400'}>Low Quality Points:</span>
                <span className="text-pink-600 font-medium ml-2">
                  {((lowQualityPointsCount / pagination.totalItems) * 100).toFixed(1)}% ({lowQualityPointsCount} points)
                </span>
              </span>
              <span className="md:hidden text-pink-600 font-medium ml-2">
                {((lowQualityPointsCount / pagination.totalItems) * 100).toFixed(1)}% ({lowQualityPointsCount} pnts)
              </span>
            </div>
          </div>

          {/* Additional Filters */}
          <div className="flex flex-wrap gap-2">
            <select
              value={qualityFilter}
              onChange={(e) => setQualityFilter(e.target.value)}
              className={`w-full md:w-auto text-sm px-2 md:px-3 py-1 md:py-2 rounded-lg border backdrop-blur-sm ${
                theme === 'light' 
                  ? 'border-gray-300/50 bg-white/80 text-gray-900' 
                  : 'border-gray-700/50 bg-gray-800/80 text-gray-100'
              }`}
            >
              <option value="All">Quality Rate: All</option>
              <option value="90">Quality Rate: &lt; 90%</option>
              <option value="75">Quality Rate: &lt; 75%</option>
              <option value="50">Quality Rate: &lt; 50%</option>
              <option value="25">Quality Rate: &lt; 25%</option>
            </select>

            <select
              value={lastUpdatedFilter}
              onChange={(e) => setLastUpdatedFilter(e.target.value)}
              className={`w-full md:w-auto text-sm px-2 md:px-3 py-1 md:py-2 rounded-lg border backdrop-blur-sm ${
                theme === 'light' 
                  ? 'border-gray-300/50 bg-white/80 text-gray-900' 
                  : 'border-gray-700/50 bg-gray-800/80 text-gray-100'
              }`}
            >
              <option value="All">Last Change: All</option>
              <option value="1h">Last Change: &gt; 1h ago</option>
              <option value="6h">Last Change: &gt; 6h ago</option>
              <option value="12h">Last Change: &gt; 12h ago</option>
              <option value="1d">Last Change: &gt; 1d ago</option>
              <option value="1w">Last Change: &gt; 1w ago</option>
            </select>

            <select
              value={diagnosisFilter}
              onChange={(e) => setDiagnosisFilter(e.target.value)}
              className={`w-full md:w-auto text-sm px-2 md:px-3 py-1 md:py-2 rounded-lg border backdrop-blur-sm ${
                theme === 'light' 
                  ? 'border-gray-300/50 bg-white/80 text-gray-900' 
                  : 'border-gray-700/50 bg-gray-800/80 text-gray-100'
              }`}
            >
              <option value="All">Diagnosis: All</option>
              <option value="Good">Diagnosis: Good</option>
              <option value="Stagnant">Diagnosis: Stagnant</option>
              <option value="Low quality">Diagnosis: Low quality</option>
              <option value="Excluded">Diagnosis: Excluded</option>
            </select>

            <select
              value={natureFilter}
              onChange={(e) => setNatureFilter(e.target.value)}
              className={`w-full md:w-auto text-sm px-2 md:px-3 py-1 md:py-2 rounded-lg border backdrop-blur-sm ${
                theme === 'light' 
                  ? 'border-gray-300/50 bg-white/80 text-gray-900' 
                  : 'border-gray-700/50 bg-gray-800/80 text-gray-100'
              }`}
            >
              <option value="All">Nature: All</option>
              <option value="Digital">Nature: Digital</option>
              <option value="Analog">Nature: Analog</option>
            </select>
          </div>
        </div>
      </div>

      {/* Points Table */}
      <div className={`max-h-[calc(100vh-240px)] flex-1 border rounded-lg backdrop-blur-sm ${
        theme === 'light' ? 'border-gray-200/50 bg-white/80' : 'border-gray-700/50 bg-gray-800/80'
      }`}>
        <div className="min-w-full">
          <div className="max-h-[calc(100vh-240px)] overflow-y-auto">
            <table className="w-full">
              <thead className={`sticky top-0 z-10 backdrop-blur-sm ${
                theme === 'light' ? 'bg-gray-50/80' : 'bg-gray-800/80'
              }`}>
                <tr>
                  <th className={`px-2 md:px-4 py-2 text-left whitespace-nowrap text-xs md:text-sm ${
                    theme === 'light' ? 'text-gray-700' : 'text-gray-200'
                  }`}>Name</th>
                  <th className={`px-2 md:px-4 py-2 text-left whitespace-nowrap text-xs md:text-sm ${
                    theme === 'light' ? 'text-gray-700' : 'text-gray-200'
                  }`}>Location</th>
                  {canViewRealTimeData && (
                    <th className={`px-2 md:px-4 py-2 text-left whitespace-nowrap text-xs md:text-sm ${
                      theme === 'light' ? 'text-gray-700' : 'text-gray-200'
                    }`}>Present Value</th>
                  )}
                  <th className={`px-2 md:px-4 py-2 text-left whitespace-nowrap text-xs md:text-sm ${
                    theme === 'light' ? 'text-gray-700' : 'text-gray-200'
                  }`}>Diagnosis</th>
                  {enabledColumns.includes('tag') && (
                    <th className={`px-2 md:px-4 py-2 text-left whitespace-nowrap text-xs md:text-sm ${
                      theme === 'light' ? 'text-gray-700' : 'text-gray-200'
                    }`}>Tag</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {points.map(point => (
                  <tr key={point.id} className={`border-t ${
                    theme === 'light' ? 'border-gray-200/30' : 'border-gray-700/30'
                  }`}>
                    <td className={`px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm ${
                      theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                    }`}>
                      <div className="flex items-center gap-2">
                        {point.type === 'ControlPoint' ? (
                          <SlidersHorizontal className="w-4 h-4 text-violet-400" />
                        ) : (
                          <Gauge className="w-4 h-4 text-blue-400" />
                        )}
                        {point.name}
                      </div>
                    </td>
                    <td className={`px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm ${
                      theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                    }`}>{point.location_name}</td>
                    {canViewRealTimeData && (
                      <td className={`px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm ${
                        theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                      }`}>
                        {pointReadings[point.tag]?.value !== undefined ? (
                          <>
                            {pointReadings[point.tag].value === "True" || pointReadings[point.tag].value === true || (pointReadings[point.tag]?.data_type === 'Digital' && (pointReadings[point.tag].value === 1 || pointReadings[point.tag].value === "1")) ? (
                              <span className="text-emerald-500">ON</span>
                            ) : pointReadings[point.tag].value === "False" || pointReadings[point.tag].value === false || (pointReadings[point.tag]?.data_type === 'Digital' && (pointReadings[point.tag].value === 0 || pointReadings[point.tag].value === "0")) ? (
                              <span className="text-rose-500">OFF</span>
                            ) : (
                              pointReadings[point.tag]?.data_type === 'Float' ? 
                                parseFloat(pointReadings[point.tag].value).toFixed(2) : 
                                pointReadings[point.tag]?.value
                            )}
                            <sub className="ml-1 text-xs text-gray-500">
                              (<span className={pointReadings[point.tag]?.quality >= 192 ? 'text-emerald-500' : 'text-amber-500'}>
                                {pointReadings[point.tag]?.quality}
                              </span>)
                            </sub>
                          </>
                        ) : 'NA'}
                      </td>
                    )}
                    <td className={`px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm ${
                      theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                    }`}>
                      {point?.diagnosis ? (
                        <div className="flex flex-col space-y-1">
                          <span className="font-medium text-blue-500">{point.diagnosis}</span>
                          {point.last_updated && (
                            <div className="flex items-center space-x-2 text-xs">
                              <span className={theme === 'light' ? 'text-gray-500' : 'text-gray-400'}>Last Change:</span>
                              <span className={`px-2 py-0.5 rounded-full backdrop-blur-sm ${
                                theme === 'light' ? 'bg-gray-100/50' : 'bg-gray-700/50'
                              }`} title={new Date(point.last_updated).getFullYear() < 2000 ? 'NA' : new Date(point.last_updated).toLocaleString()}>
                                {(() => {
                                  const now = new Date();
                                  const updated = new Date(point.last_updated);
                                  
                                  if (updated.getFullYear() < 2000) {
                                    return 'NA';
                                  }
                                  
                                  const diffSeconds = Math.floor((now - updated) / 1000);
                                  
                                  if (diffSeconds < 60) return `${Math.max(diffSeconds, 1)}s ago`;
                                  
                                  const diffMinutes = Math.floor(diffSeconds / 60);
                                  if (diffMinutes < 60) return `${diffMinutes}m ago`;
                                  
                                  const diffHours = Math.floor(diffMinutes / 60);
                                  if (diffHours < 24) return `${diffHours}h ago`;
                                  
                                  const diffDays = Math.floor(diffHours / 24);
                                  if (diffDays < 7) return `${diffDays}d ago`;
                                  
                                  const diffWeeks = Math.floor(diffDays / 7);
                                  return `${diffWeeks}w ago`;
                                })()}
                              </span>
                              <span className={theme === 'light' ? 'text-gray-500' : 'text-gray-400'}>Quality Rate:</span>
                              <span className={`px-2 py-0.5 rounded-full backdrop-blur-sm ${
                                point.quality_rate >= 90 
                                  ? theme === 'light' ? 'bg-emerald-100/50 text-emerald-800' : 'bg-emerald-900/50 text-emerald-200'
                                  : point.quality_rate >= 70 
                                    ? theme === 'light' ? 'bg-amber-100/50 text-amber-800' : 'bg-amber-900/50 text-amber-200'
                                    : theme === 'light' ? 'bg-rose-100/50 text-rose-800' : 'bg-rose-900/50 text-rose-200'
                              }`}>
                                {point.quality_rate.toFixed(2)}%
                              </span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className={theme === 'light' ? 'text-gray-500' : 'text-gray-400'}>NA</span>
                      )}
                    </td>
                    {enabledColumns.includes('tag') && (
                      <td className={`px-2 md:px-4 py-2 whitespace-nowrap text-xs md:text-sm ${
                        theme === 'light' ? 'text-gray-900' : 'text-gray-100'
                      }`}>
                        {point.tag}
                        <button onClick={() => copyToClipboard(point.tag, point.id)} className={`ml-2 ${
                          theme === 'light' ? 'text-gray-500 hover:text-gray-700' : 'text-gray-400 hover:text-gray-200'
                        }`}>
                          <Clipboard className="w-4 md:w-5 h-4 md:h-5" />
                        </button>
                        {copied[point.id] && <span className="ml-2 text-emerald-500">Copied!</span>}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Pagination */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-2 md:gap-0">
        <div className="flex flex-col md:flex-row items-start md:items-center gap-2 md:gap-4 w-full md:w-auto">
          <span className={`text-xs md:text-sm ${
            theme === 'light' ? 'text-gray-600' : 'text-gray-400'
          }`}>
            Showing {(pagination.currentPage - 1) * pagination.pageSize + 1} to {
              Math.min(pagination.currentPage * pagination.pageSize, pagination.totalItems)
            } of {pagination.totalItems} entries
          </span>
          
          <select
            value={pagination.pageSize}
            onChange={(e) => setPagination(prev => ({ ...prev, pageSize: Number(e.target.value), currentPage: 1 }))}
            className={`w-full md:w-auto px-2 py-1 rounded border backdrop-blur-sm text-xs md:text-sm ${
              theme === 'light' 
                ? 'border-gray-300/50 bg-white/80 text-gray-900' 
                : 'border-gray-700/50 bg-gray-800/80 text-gray-100'
            }`}
          >
            <option value={20}>20 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
        <div className="flex flex-wrap gap-1 md:gap-2">
          <button
            onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.max(1, prev.currentPage - 1) }))}
            disabled={pagination.currentPage === 1}
            className={`px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
              pagination.currentPage === 1
                ? theme === 'light' 
                  ? 'border-gray-300/50 text-gray-400 cursor-not-allowed'
                  : 'border-gray-700/50 text-gray-600 cursor-not-allowed'
                : theme === 'light'
                  ? 'border-gray-300/50 text-gray-600 hover:bg-gray-100/50'
                  : 'border-gray-700/50 text-gray-400 hover:bg-gray-800/50'
            }`}
          >
            Previous
          </button>
          
          {getPageNumbers().map((page, index) => (
            typeof page === 'number' ? (
              <button
                key={`page-${page}`}
                onClick={() => setPagination(prev => ({ ...prev, currentPage: page }))}
                className={`px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
                  pagination.currentPage === page
                    ? 'border-blue-500/30 text-blue-500 bg-blue-500/10'
                    : theme === 'light'
                      ? 'border-gray-300 text-gray-600 hover:bg-gray-100'
                      : 'border-gray-700 text-gray-400 hover:bg-gray-800'
                }`}
              >
                {page}
              </button>
            ) : (
              <span key={`ellipsis-${index}`} className={`px-2 md:px-2.5 py-1 text-xs font-medium ${
                theme === 'light' ? 'text-gray-600' : 'text-gray-400'
              }`}>
                ...
              </span>
            )
          ))}
          
          <button
            onClick={() => setPagination(prev => ({ ...prev, currentPage: Math.min(prev.totalPages, prev.currentPage + 1) }))}
            disabled={pagination.currentPage === pagination.totalPages}
            className={`px-2 md:px-2.5 py-1 text-xs font-medium border backdrop-blur-sm rounded-full transition-all duration-200 flex items-center gap-1 whitespace-nowrap ${
              pagination.currentPage === pagination.totalPages
                ? 'border-gray-500/30 text-gray-500 cursor-not-allowed'
                : 'border-gray-500/30 text-gray-500 hover:bg-gray-500/10'
            }`}
          >
            Next
          </button>
        </div>
      </div>
    </div>
  );
}

export default PointList;
